import * as LoanDomain from "./loan-domain"
import * as ProspectiveDomain from "./prospective-domain"
import * as PpiDomain from "./ppi-domain"
import { getGrossCashFlowAmount } from "./cash-flow-domain"
import {
    Account,
    LoanWithDecomposition,
    Ppi,
    PpiInvestmentProject,
    PpiInvestmentProjectGrant,
    PpiInvestmentProjectGrantYear,
    PpiInvestmentProjectYear,
    PpiInvestmentTheme,
    PpiYear,
    PpiYearLoan,
    ProjectInduced,
    Loan,
    Scenario,
    LocalAuthority,
    LocalAuthorityChapterAccount
} from "../../model"
import Operations from "../../component/scenarii/common/operations"
import * as Tabledefinition from "../../component/scenarii/detail/table-definition"

/**
 * Calcul la ligne Equilibre ppi du ppi pour les années prospectives.
 * @param loans
 * @param localAuthority
 * @param ppi
 * @param scenario
 * @param year
 */
export function calculateBalancePpiProAmount(
    loans: Loan[],
    localAuthority: LocalAuthority,
    ppi: Ppi,
    scenario: Scenario,
    year: number,
): number {
    const decompositions = LoanDomain.getDecompositions(loans)
    const result = calculateTotalPpiByYear(ppi, loans, scenario, localAuthority, year)
    // const selfFinancing = ProspectiveDomain.calculateGrossCashFlow(localAuthority, scenario, ppi, decompositions, year)
    const operations: Operations = new Operations(scenario, ppi, localAuthority);
    const ppiYearLoans = PpiDomain.getPpiYearLoans(LoanDomain.getLoansWithDecomposition(loans), ppi)
    const selfFinancing = Tabledefinition._aggregateCAFBrute("prospective", year, localAuthority, operations, null, [], ppiYearLoans);

    return result + selfFinancing
}

/**
 * Calcul la ligne Equilibre ppi du ppi pour les années rétrospectives.
 * @param loans
 * @param localAuthority
 * @param ppi
 * @param scenario
 * @param year
 */
export function calculateBalancePpiRetroAmount(
    loans: Loan[],
    localAuthority: LocalAuthority,
    ppi: Ppi,
    scenario: Scenario,
    year: number,
): number {
    const decompositions = LoanDomain.getDecompositions(loans)
    let observations = localAuthority.observations
    observations = localAuthority.observations.filter(o => localAuthority.accounts.some(a => a.account.id === o.account.id));
    const result = ProspectiveDomain.calculateResult(localAuthority, scenario, ppi, decompositions, year)
    const selfFinancing = ProspectiveDomain.getSelfFinancingAmount(observations, year)

    return result + selfFinancing
}

/**
 * Calcule le total des recettes moins les investissements dans le ppi (à différencier des recettes et des dépenses prospectives).
 */
export function calculateTotalPpiByYear(
    ppi: Ppi,
    loans: Loan[],
    scenario: Scenario,
    localAuthority: LocalAuthority,
    year: number
): number {
    const revenue = calculateTotalRevenueAmount(ppi, loans, scenario, localAuthority, year)
    const expense = calculateTotalSpendingAmount(ppi, loans, scenario, localAuthority, year)
    return revenue - expense
}

/**
 * Calcule la totalité des charges induites du PPI passé en paramètre avec la
 * possibilité de filtrer par compte et/ou par année.
 * @param ppi
 * @param account
 * @param year
 * @param receipts
 */
export function calculateInduced(
    ppi: Ppi,
    account?: Account,
    year?: number,
    receipts?: boolean
): number {
    const inducedProjects = findInducedProjects(ppi, account, year)
    return inducedProjects.reduce((induced, inducedProject) => {
        //return induced + (receipts ? (inducedProject.gain || 0) : (inducedProject.loss || 0))
        const gainLoss = inducedProject.gain || -inducedProject.loss || 0;
        return induced + gainLoss;
    }, 0)
}

export function filterProjectsByIds(
    projects: PpiInvestmentProject[],
    ids: number[]
): PpiInvestmentProject[] {
    return projects.filter(p => p && ids.some(id => id === p.id))
}

/**
 * Filtre les thèmes et leurs sous-thèmes selon la liste d'id passée en
 * paramètre.
 *
 * @param themes Liste des thèmes à filtrer.
 * @param ids Liste des identifiant des thèmes à filtrer.
 */
export function filterThemesAndSubthemesByIds(
    themes: PpiInvestmentTheme[],
    ids: number[]
): PpiInvestmentTheme[] {
    return themes.reduce<PpiInvestmentTheme[]>((filteredThemes, theme) => {
        if (theme && ids.some(id => id === theme.id))
            return [...filteredThemes, theme]

        if (theme.themes && theme.themes.length)
            return [...filteredThemes, ...filterThemesByIds(theme.themes, ids)]

        return filteredThemes
    }, [])
}

export function filterThemesByIds(
    themes: PpiInvestmentTheme[],
    ids: number[],
): PpiInvestmentTheme[] {
    return themes.filter(t => t && ids.some(id => id === t.id))
}

export function filterGrantsByType(
    grants: PpiInvestmentProjectGrant[],
    awarded: boolean
): PpiInvestmentProjectGrant[] {
    return grants.filter(grant => grant.awarded === awarded)
}

/**
 * Filtre la liste de projects induits par compte et/ou par année.
 * @param inducedProjects
 * @param account
 * @param year
 */
export function filterInducedProjects(
    inducedProjects: ProjectInduced[],
    account?: Account,
    year?: number
): ProjectInduced[] {
    const filteredInducedProject = inducedProjects.filter(iP => {
        // Si le compte et l'année sont indéfinis, aucun filtre n'est appliqué
        if (undefined === account && undefined === year) return true

        // Si le compte et l'année sont définis, les deux doivent concorder
        if (undefined !== account && undefined !== year)
            return account.id === iP.induced.id && year === iP.year

        // Si seul le compte est défini, il doit concorder
        if (undefined !== account) return account.id === iP.induced.id

        // Si seule l'année est définis, elle doit concorder
        if (undefined !== year) return year === iP.year

        // Tous les autres cas sont faux
        return false
    })

    return filteredInducedProject
}

/**
 * Filtre les années de PPI `ppiYears` selon les années `years`.
 * @param ppiYears
 * @param years
 */
export function filterPpiYearsByYears(
    ppiYears: PpiYear[],
    years: number[]
): PpiYear[] {
    return ppiYears.filter(ppiYear => ppiYear && years.some(y => y === ppiYear.year))
}

export function findFctvaRetrieveYears(
    ppiYears: PpiYear[],
    year: number
): number[] {
    return ppiYears.reduce<number[]>((years, ppiYear) => {
        if ((ppiYear.fctvaRetrieve || 0) + ppiYear.year === year)
            years.push(ppiYear.year)

        return years
    }, [])
}

export function findGrantYearByYear(
    grantYears: PpiInvestmentProjectGrantYear[],
    year: number
): PpiInvestmentProjectGrantYear | null {
    return grantYears.find(grantYear => grantYear.year === year) || null
}

/**
 * Trouve la totalité des projets induits du PPI passé en paramètre avec la
 * possibilité de filtrer par compte et/ou par année.
 * @param ppi
 * @param account
 * @param year
 */
export function findInducedProjects(
    ppi: Ppi,
    account?: Account,
    year?: number,
): ProjectInduced[] {
    return ppi.investment_themes.reduce((inducedProjects, theme) => {
        const themeInducedProjects = findThemeInducedProjects(theme, account, year)
        return [...inducedProjects, ...themeInducedProjects]
    }, [] as ProjectInduced[])
}

/**
 * Trouve le thème parent de `theme` dans la liste `themes`.
 * @param theme
 * @param themes
 * @returns Le thème parent s'il en est, sinon `null`.
 */
export function findParentTheme(
    theme: PpiInvestmentTheme,
    themes: PpiInvestmentTheme[]
): PpiInvestmentTheme | null {
    for (const parentTheme of themes) {
        if (themeOwnsTheme(parentTheme, theme)) return parentTheme
    }
    return null
}

export function findThemeById(
    themes: PpiInvestmentTheme[],
    id: number
): PpiInvestmentTheme | null {
    return themes.find(t => t.id === id) || null
}

export function findThemeFromProject(
    themes: PpiInvestmentTheme[],
    project: PpiInvestmentProject
): PpiInvestmentTheme | null {
    for (const subTheme of themes) {
        if (themeOwnsProject(subTheme, project)) return subTheme
        if (subTheme.themes) {
            const theme = findThemeFromProject(subTheme.themes, project)
            if (theme) return theme
        }
    }

    return null
}

/**
 * Trouve la totalité des projets induits du thème passé en paramètre avec la
 * possibilité de filtrer par compte et/ou par année.
 * @param theme
 * @param account
 * @param year
 */
export function findThemeInducedProjects(
    theme: PpiInvestmentTheme,
    account?: Account,
    year?: number,
): ProjectInduced[] {
    const childInducedProjects = theme.projects.reduce(
        (prevInducedProjects, project) => {
            if (project.visible){
                return [
                    ...prevInducedProjects,
                    ...filterInducedProjects(project.induceds, account, year)
                ]
            }
            return prevInducedProjects;
        },
        [] as ProjectInduced[]
    )

    const descendingInducedProjects = theme.themes.reduce(
        (prevInducedProjects, subTheme) => {
            return [
                ...prevInducedProjects,
                ...findThemeInducedProjects(subTheme, account, year)
            ]
        },
        [] as ProjectInduced[]
    )

    return [...childInducedProjects, ...descendingInducedProjects]
}

export function findPpiYearByYear(
    ppiYears: PpiYear[],
    year: number
): PpiYear | null {
    return ppiYears.find(ppiYear => ppiYear.year === year) || null
}

export function findPpiYearLoanByYear(
    ppiYearLoans: PpiYearLoan[],
    year: number
): PpiYearLoan | null {
    return ppiYearLoans.find(ppiYearLoan => ppiYearLoan.year === year) || null
}

export function findProjectYearByYear(
    projectYears: PpiInvestmentProjectYear[],
    year: number
): PpiInvestmentProjectYear | null {
    return projectYears.find(pYear => pYear.year === year) || null
}

export function findThemeProjectsByIds(
    theme: PpiInvestmentTheme,
    projectIds: number[]
): PpiInvestmentProject[] {
    let projects: PpiInvestmentProject[] = []

    projects = projects.concat(filterProjectsByIds(theme.projects, projectIds))

    if (theme.themes) for (const subTheme of theme.themes) {
        projects = projects.concat(findThemeProjectsByIds(subTheme, projectIds))
    }

    return projects
}

export function findThemesProjectsByIds(
    themes: PpiInvestmentTheme[],
    projectIds: number[]
): PpiInvestmentProject[] {
    return themes.reduce<PpiInvestmentProject[]>((projects, theme) => {
        return projects.concat(findThemeProjectsByIds(theme, projectIds))
    }, [])
}

/**
 * Applatit la hiérachie complète des thèmes passés en paramètre.
 * @param themes La hiérarchie de thèmes à applatir.
 */
export function flattenThemes(
    themes: PpiInvestmentTheme[]
): PpiInvestmentTheme[] {
    if (!(themes && themes.length)) return []

    return themes.reduce<PpiInvestmentTheme[]>((flattenedThemes, theme) => {
        if (theme) flattenedThemes.push(theme)
        flattenedThemes.concat(flattenThemes(theme.themes))

        return flattenedThemes
    }, [])
}

export function getGrantAmountsByYears(
    grant: PpiInvestmentProjectGrant,
    years: number[]
): number[] {
    return years.map<number>(year => {
        const grantYear = findGrantYearByYear(grant.years, year)
        return grantYear ? (grantYear.amount || 0) : 0
    })
}

/**
 * Calcul le budget des subventions du projet passé en paramètre.
 * @param project
 * @param awarded Si `true`, calcule le budget des subventions octroyées
 * uniquement. Si `false`, calcule le budget des subventions demandées
 * uniquement. Sinon (si `undefined`), calcule le budget de toutes les subventions.
 */
export function getGrantsBudget(
    project: PpiInvestmentProject,
    awarded?: boolean
): number {
    if (undefined === awarded)
        return project.grants.reduce<number>((budget, g) => budget + g.budget, 0)

    return project.grants.reduce<number>((budget, g) =>
        g.awarded === awarded ? budget + g.budget : 0
        , 0)
}

export function getNetCostBudget(
    ppi: Ppi,
    project: PpiInvestmentProject,
    years: number[]
): number {
    return (project.budget || 0)
        - getGrantsBudget(project)
        - getProjectFctva(ppi, project)
}

export function getPpiYearLoans(
    decomposedLoans: LoanWithDecomposition[],
    ppi: Ppi
): PpiYearLoan[] {
    return ppi.years.reduce<PpiYearLoan[]>((yearLoans, ppiYear) => {
        let interests = 0
        let reimbourse = 0
        let remaining = 0
        let total = 0

        for (const decomposedLoan of decomposedLoans) {
            interests += LoanDomain.getYearInterest(
                decomposedLoan.decomposition,
                ppiYear.year
            )
            reimbourse += LoanDomain.getYearReimboursedCapital(
                decomposedLoan.decomposition,
                ppiYear.year
            )
            remaining += LoanDomain.getYearRemainingCapital(
                decomposedLoan.decomposition,
                ppiYear.year
            )

            if (ppiYear.year === decomposedLoan.loan.contractedOn.getFullYear())
                total += decomposedLoan.loan.amount
        }

        yearLoans.push({
            interests, reimbourse, remaining, total, year: ppiYear.year
        })
        return yearLoans
    }, [])
}

export function getProjectFctva(
    ppi: Ppi,
    project: PpiInvestmentProject
): number {
    return ppi.years.reduce<number>((totalFctva, ppiYear) => {
        return totalFctva + getProjectFctvaAmountByYear(ppi, project, ppiYear.year)
    }, 0)
}

export function getProjectFctvaAmountByYear(
    ppi: Ppi,
    project: PpiInvestmentProject,
    year: number
): number {
    if (project.fctva) {
        const retrieveYears = findFctvaRetrieveYears(ppi.years, year)

        return retrieveYears.reduce<number>((fctvaAmount, retrieveYear) => {
            const projectYear = findProjectYearByYear(project.years, retrieveYear)
            const ppiYear = findPpiYearByYear(ppi.years, year)
            const fctva = ppiYear && (ppiYear.fctva || ppi.fctva)

            return projectYear
                ? fctvaAmount + projectYear.amount * fctva / 100
                : fctvaAmount
        }, 0)
    }

    return 0
}

/**
 * Vérifie si le PPI passé en paramètre possède des projets induits
 * correspondant au compte et à l'année passés en paramètres.
 * @param ppi
 * @param account
 * @param year
 */
export function ppiHasInducedProjects(
    ppi: Ppi,
    account: Account,
    year: number
): boolean {
    return ppi != null && ppi.investment_themes != null && ppi.investment_themes.some(theme => themeHasInducedProjects(
        theme, account, year))
}

export function sortProjectsByName(
    projects: PpiInvestmentProject[],
    asc = true
): PpiInvestmentProject[] {
    return projects.sort((p1, p2) => {
        if (!p1 && !p2) return 0
        if (p1 && !p2) return asc ? 1 : -1
        if (p2 && !p1) return asc ? -1 : 1
        if (p1.name === p2.name) return 0
        return p1.name > p2.name && asc ? 1 : -1
    })
}

export function sortThemesByName(
    themes: PpiInvestmentTheme[],
    asc = true
): PpiInvestmentTheme[] {
    return themes.sort((t1, t2) => {
        if (!t1 && !t2) return 0
        if (t1 && !t2) return asc ? 1 : -1
        if (t2 && !t1) return asc ? -1 : 1
        if (t1.name === t2.name) return 0
        return t1.name > t2.name && asc ? 1 : -1
    })
}

export function sumGrantAmounts(grant: PpiInvestmentProjectGrant): number {
    return grant.years.reduce<number>((sum, gY) => sum + (gY.amount || 0), 0)
}

export function sumGrantsAmounts(
    project: PpiInvestmentProject,
    awarded: boolean
): number {
    return filterGrantsByType(project.grants, awarded)
        .reduce<number>((sum, grant) => sum + sumGrantAmounts(grant), 0)
}

export function sumGrantsAmountsByYear(
    project: PpiInvestmentProject,
    awarded: boolean,
    year: number
): number {
    return project.grants.reduce<number>((sum, grant) => {
        const grantYear = findGrantYearByYear(grant.years, year)
        return grant.awarded === awarded && grantYear
            ? sum + grantYear.year : sum
    }, 0)
}

export function sumGrantsAmountsByYears(
    project: PpiInvestmentProject,
    awarded: boolean,
    years: number[]
): number[] {
    return years.map<number>(year =>
        sumGrantsAmountsByYear(project, awarded, year))
}

export function sumProjectAmounts(project: PpiInvestmentProject): number {
    return project.years.reduce<number>((sum, pY) => sum + (pY.amount || 0), 0)
}

/**
 * Vérifie si le thème passé en paramètre possède des projets induits
 * correspondant au compte et à l'année passés en paramètres.
 * @param theme
 * @param account
 * @param year
 */
export function themeHasInducedProjects(
    theme: PpiInvestmentTheme,
    account: Account,
    year: number
): boolean {
    if (theme.projects.length) {
    const hasInProjects = theme.projects.some(project =>
        project.induceds.some(iP =>
            year === iP.year && account.id === iP.induced.id
        )
    )
    if (hasInProjects) return true
}

if (theme.themes.length) {
    return theme.themes.some(subTheme =>
        themeHasInducedProjects(subTheme, account, year)
    )
}

return false
}

export function themeOwnsProject(
    theme: PpiInvestmentTheme,
    project: PpiInvestmentProject,
    directChild = false
): boolean {
    return theme.projects.some(themeProject => {
        if (directChild) return themeProject.id === project.id
        if (themeProject.id === project.id) return true
        return theme.themes.some(subTheme => themeOwnsProject(subTheme, project))
    })
}

export function themeOwnsTheme(
    theme: PpiInvestmentTheme,
    searchedTheme: PpiInvestmentTheme
): boolean {
    if (!(theme && theme.themes && theme.themes.length && searchedTheme))
        return false

    return theme.themes.some(subtheme => subtheme && subtheme.id === searchedTheme.id)
}

/**
 * Calcule le montant total des recettes par l'année passée en paramètre.
 * @param ppi
 * @param loans
 * @param scenario
 * @param localAuthority
 * @param year
 */
export function calculateTotalRevenueAmount(
    ppi: Ppi,
    loans: Loan[],
    scenario: Scenario,
    localAuthority: LocalAuthority,
    year: number
): number {
    let totalRevenue = 0

    ppi.revenues.map(r => {
        const ppiYear = r.years.find(y => y.year == year)
        totalRevenue += ppiYear ? ppiYear.value : 0
    })

    const ppiYear = ppi.years.find(y => y.year == year)

    if (scenario && year < scenario.year) {
        // Autofinancement
        totalRevenue += getGrossCashFlowAmount(localAuthority.observations, year) || 0
    } else if (scenario && year >= scenario.year) {
        const decompositions = LoanDomain.getDecompositions(loans)
        // Autofinancement
        totalRevenue += ProspectiveDomain.calculateGrossCashFlow(localAuthority, scenario, ppi, decompositions, year) || 0
    } else if (ppiYear) {
        // Autofinancement
        totalRevenue += ppiYear.selfFinancing || 0
        // Ventes de patrimoine
        totalRevenue += ppiYear.saleOfHeritage || 0
        // Recettes d'opération pour compte de tiers
        totalRevenue += ppiYear.tpoRevenue || 0
        // Taxe d'aménagement
        totalRevenue += ppiYear.planningFee || 0
        // Subventions octroyées
        totalRevenue += getGrantsAmount(true, ppiYear.year, ppi)
        // Subventions demandées
        totalRevenue += getGrantsAmount(false, ppiYear.year, ppi)
    }

    // Reprise de l'exédent N-1
    totalRevenue += getPreviousSurplus(ppi, loans, scenario, localAuthority, year)
    // FCTVA
    totalRevenue += calculateFctva(year, ppi)
    // Nominal des emprunts
    totalRevenue += LoanDomain.getTotalNominal(loans, year)

    return totalRevenue
}

/**
 * Récupère le surplus de l'année précèdente s'il existe.
 */
export function getPreviousSurplus(
    ppi: Ppi,
    loans: Loan[],
    scenario: Scenario,
    la: LocalAuthority,
    year: number,
): number {
    if (ppi.years[0].year == year) {
        return ppi.years[0].previousSurplus || 0
    } else {
        let result = 0
        if (ppi.years.find(y => y.year == year - 1)) {
            result = calculateTotalPpiByYear(ppi, loans, scenario, la, year - 1)
        }
        return result > 0 ? result : 0
    }
}

/**
 * Calcule de la Fctva par l'année passée en paramètre.
 * @param year
 * @param ppi
 */
export function calculateFctva(
    year: number,
    ppi: Ppi
): number {
    let result = 0
    let fctvaRetrieve
    let toRetrieve = ppi.years.filter(y => {
        fctvaRetrieve = y.fctvaRetrieve
        if (fctvaRetrieve == null || fctvaRetrieve == undefined) {
            fctvaRetrieve = ppi.fctvaRetrieve
        }
        return (fctvaRetrieve + y.year) === year
    })
    let fctva
    toRetrieve.map(y => {
        fctva = ppi.years.find(py => py.year == y.year).fctva
        if (fctva == null || fctva == undefined) {
            fctva = ppi.fctva
        }

        result += getTotalInvests(year, ppi, true) * (fctva / 100)
    })

    return result
}

/**
 * Récupère la somme des Grants par l'année passée en paramètre.
 * @param awarded
 * @param year
 * @param ppi
 */
export function getGrantsAmount(
    awarded: boolean,
    year: number,
    ppi: Ppi): number {
    let result = 0
    ppi.investment_themes.map(t => {
        t.projects.map(p => {
            if (p.visible) {
                p.grants.filter(g => g.awarded == awarded).map(g => {
                    const grantYear = g.years.find(y => y.year == year)
                    result += grantYear ? grantYear.amount : 0
                })
            }
        })
        if (t.themes.length > 0) {
            t.themes.map(t => {
                t.projects.map(p => {
                    if (p.visible) {
                        p.grants.filter(g => g.awarded == awarded).map(g => {
                            const grantYear = g.years.find(y => y.year == year)
                            result += grantYear ? grantYear.amount : 0
                        })
                    }
                })
            })
        }
    })
    return result
}

/**
 * Récupère le total des investissements du Ppi.
 * @param year
 * @param ppi
 * @param fctvaOnly
 */
export function getTotalInvests(
    year: number,
    ppi: Ppi,
    fctvaOnly = false
): number {
    let result = 0
    ppi.investment_themes.map(t => {
        t.projects.map(p => {
            if (p.visible && (!fctvaOnly || p.fctva)) {
                const projectYear = p.years.find(y => y.year == year)
                result += projectYear ? projectYear.amount : 0
            }
        })
        t.themes.map(st => {
            st.projects.map(p => {
                if (p.visible && (!fctvaOnly || p.fctva)) {
                    const projectYear = p.years.find(y => y.year == year)
                    result += projectYear ? projectYear.amount : 0
                }
            })
        })
    })
    return result
}

/**
 * Calcule le montant total des dépenses du Ppi par l'année passée en paramètre.
 * @param ppi
 * @param loans
 * @param scenario
 * @param localAuthority
 * @param year
 */
export function calculateTotalSpendingAmount(
    ppi: Ppi,
    loans: Loan[],
    scenario: Scenario,
    localAuthority: LocalAuthority,
    year: number
): number {
    const decompositions = LoanDomain.getDecompositions(loans)
    const reimbourse = LoanDomain.getTotalYearReimbursedCapital(decompositions, year)
    let result = 0

    ppi.expenses.map(e => {
        const ppiExpenseYear = e.years.find(y => y.year == year)
        result += ppiExpenseYear ? ppiExpenseYear.value : 0
    })

    const ppiYear = ppi.years.find(y => y.year === year)
    result += ppiYear ? ppiYear.tpoSpending || 0 : 0
    result += reimbourse
    result += getTotalInvests(year, ppi)
    result += getDeficitRecovery(ppi, loans, scenario, localAuthority, year)

    return result
}

/**
 * Récupère le remboursement de la dette par l'année passée en paramètre.
 * @param ppi
 * @param loans
 * @param scenario
 * @param localAuthority
 * @param year
 */
export function getDeficitRecovery(
    ppi: Ppi,
    loans: Loan[],
    scenario: Scenario,
    localAuthority: LocalAuthority,
    year: number,
): number {
    if (ppi.years[0].year == year) {
        return ppi.years[0].deficitRecovery || 0
    } else {
        var result = 0
        if (ppi.years.find(y => y.year == year - 1)) {
            result = calculateTotalPpiByYear(ppi, loans, scenario, localAuthority, year - 1)
        }
        return result < 0 ? (result * -1) : 0
    }
}

export function calculateOrderOperationsTotalProspective(
    year: number,
    la: LocalAuthority,
    ppi: Ppi,
    loans: Loan[],
    receipts: boolean,
    scenario: Scenario
): number {
    let result = 0
    const loanDecompositions = LoanDomain.getDecompositions(loans)
    let accounts = getOrderOperationAccounts(la, receipts)
    accounts.map(laca => {
        result += ProspectiveDomain.findAccountAmount(la, laca.account, laca.chapter ? laca.chapter : null, scenario, ppi, loanDecompositions, receipts, year)
    })
    return result
}

export function getOrderOperationAccounts(
    la,
    receipts
): LocalAuthorityChapterAccount[] {
    let ok
    let numbers = ["68*", "78*", "675*", "676*", "775*", "776*", "777*"]
    return la.accounts.filter(laca => {
        ok = false
        if (receipts == undefined || receipts == null || (receipts && laca.account.receipts) || (!receipts && laca.account.expense)) {
            numbers.map(on => {
                if (!ok) {
                    if (on.indexOf("*") == -1) {
                        ok = (laca.chapter && laca.chapter.number == on)
                    } else {
                        ok = laca.account.number.startsWith(on.substring(0, on.length - 1))
                    }
                }
            })
        }
        return ok
    })
}



// WEBPACK FOOTER //
// ./src/domain/sfp/ppi-domain.ts